import React, { useEffect, useState } from "react";
import {
  ActionButtonsContainer,
  AddBanner,
  CancelButton,
  FormContainer,
  FormTitle,
  PreviewButton,
  RemoveButton,
  Subtitle,
} from "./style";
import { z } from "zod";
import { useFieldArray, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { FaRegEye, FaRegTrashAlt } from "react-icons/fa";
import { Error, FormRow } from "../../../../components/ControlledInputs/styles";
import ControlledInput from "../../../../components/ControlledInputs/input";
import ControlledDatePicker from "../../../../components/ControlledInputs/date-picker-antd";
import { ControlledDropzoneForArray } from "../../../../components/ControlledInputs/dropzone";
import ControlledTextareaJodit from "../../../../components/ControlledInputs/jodit-textarea";
import { FormattedMessage } from "react-intl";
import ReorderableTable from "../../../../components/ReorderableTable";
import { MdError } from "react-icons/md";
import { Container, FormSeparator, Title, TitleContainer } from "../../style";
import moment from "moment";
import sanitizeHtml from "sanitize-html";

const bannerSchema = z.object({
  id: z.coerce.number().nullish(),
  title: z.string().min(1, { message: "error.field_required" }).max(180, {
    message: "error.max_length-180",
  }),
  buttonUrl: z
    .string()
    .url({ message: "error.valid_url" })
    .optional()
    .or(z.literal("")),
  startDate: z.date(),
  endDate: z.date().nullish(),
  bannerImage: z.instanceof(File).refine((file) => file.size > 0, {
    message: "file cannot be empty",
  }),
  description: z
    .string()
    .refine(
      (value) => {
        const cleanedValue = value
          .replace(/<\/?[^>]+(>|$)/g, "")
          .replace(/\n/g, "")
          .trim();

        return cleanedValue.length > 0;
      },
      {
        message: "error.field_required",
      },
    )
    .refine(
      (value) => {
        const cleanedValue = sanitizeHtml(value, { allowedTags: [] }).replace(
          /&nbsp;/g,
          "",
        );

        return cleanedValue.length <= 360;
      },
      {
        message: "error.max_length-360",
      },
    ),
});

const schema = z.object({
  banners: z.array(bannerSchema),
});

export default function HomepageForm({
  defaultValues,
  preview,
  setBanners,
  setCourses,
  eventCourses,
  choosenCourses,
  setChoosenCourses,
}) {
  const [needCourseError, setNeedCourseError] = useState(false);

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues:
      defaultValues.banners.length > 0
        ? {
            banners: defaultValues.banners.map((banner) => ({
              ...banner,
              startDate: new Date(moment(banner.startDate)),
              endDate: banner.endDate ? new Date(moment(banner.endDate)) : null,
            })),
          }
        : {
            banners: [
              {
                title: "",
                buttonUrl: "",
                bannerImage: [],
                description: "",
              },
            ],
          },
  });

  const { control, handleSubmit, formState, clearErrors, setError } = form;
  const { fields, append, remove } = useFieldArray({
    control,
    name: "banners",
  });

  useEffect(() => {
    if (choosenCourses.length === 0) {
      setChoosenCourses(eventCourses);
    }
  }, [choosenCourses.length, eventCourses, setChoosenCourses]);

  const validateVisibleCourses = () => {
    const hasCourseVisible = choosenCourses.filter(
      (course) => course.visible === true,
    );

    if (hasCourseVisible.length === 0) {
      setNeedCourseError(true);

      return false;
    }

    setNeedCourseError(null);
    return true;
  };

  const onSubmit = async ({ banners }) => {
    if (!validateVisibleCourses()) return null;
    setBanners(banners);
    setCourses(choosenCourses);
    preview();
  };

  return (
    <Container>
      <TitleContainer>
        <Title>
          <FormattedMessage id="carousel-banner" />
          <Subtitle>
            <FormattedMessage id="add_one_to_five_banners" />
          </Subtitle>
        </Title>
      </TitleContainer>

      <FormSeparator />

      <FormContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          {fields.map((item, index) => {
            const startDateWatch = form.watch(`banners.${index}.startDate`);
            const endDateWatch = form.watch(`banners.${index}.endDate`);

            return (
              <div key={item.id}>
                <FormTitle>
                  Banner {index + 1}
                  {fields.length > 1 && index !== 0 && (
                    <RemoveButton
                      type="button"
                      onClick={() => {
                        remove(index);
                      }}
                    >
                      <FaRegTrashAlt /> Remover
                    </RemoveButton>
                  )}
                </FormTitle>
                <FormRow marginBottom="24px">
                  <ControlledInput
                    name={`banners.${index}.title`}
                    control={control}
                    formState={formState}
                    label="title"
                    required
                    counter
                    arrayName="banners"
                    max={180}
                  />
                  <ControlledInput
                    name={`banners.${index}.buttonUrl`}
                    control={control}
                    formState={formState}
                    label="button_url"
                    arrayName="banners"
                    required={false}
                  />
                </FormRow>
                <FormRow zIndex="20" marginBottom={"24px"}>
                  <ControlledDatePicker
                    name={`banners.${index}.startDate`}
                    control={control}
                    label="start"
                    required
                    errors={formState.errors}
                    arrayName="banners"
                    minDate={new Date()}
                    onChange={(date) => {
                      if (date > endDateWatch) {
                        form.setValue(`banners.${index}.endDate`, date);
                      }
                    }}
                  />
                  <ControlledDatePicker
                    name={`banners.${index}.endDate`}
                    control={control}
                    label="end"
                    errors={formState.errors}
                    arrayName="banners"
                    minDate={startDateWatch ? startDateWatch : new Date()}
                  />
                </FormRow>
                <FormRow marginBottom="24px">
                  <ControlledDropzoneForArray
                    name={`banners.${index}.bannerImage`}
                    errors={formState.errors}
                    control={control}
                    required
                    arrayName="banners"
                    setValue={form.setValue}
                    label="image"
                    description="course_cover_sub"
                    subtitle="banner_cover_sub_2"
                    formMulti={true}
                    index={index}
                    defaultName={defaultValues.banners[index]?.title}
                    clearErrors={clearErrors}
                    setErrorForm={setError}
                  />
                  <ControlledTextareaJodit
                    name={`banners.${index}.description`}
                    control={control}
                    formState={formState}
                    arrayName="banners"
                    label={"description"}
                    required={true}
                    sub={"description_banner"}
                    max={360}
                    height={248}
                    minHeight={248}
                    maxHeight={248}
                    buttonsType="normal"
                    splitColumn
                  />
                </FormRow>
              </div>
            );
          })}

          <AddBanner
            type="button"
            disabled={fields.length >= 5}
            onClick={() =>
              append({
                title: "",
                buttonUrl: "",
                bannerImage: [],
                description: "",
              })
            }
          >
            <AddIcon />
            <FormattedMessage id="add_new_banner_button" />
          </AddBanner>

          <ReorderableTable
            title={"sidebar.courses"}
            data={choosenCourses}
            setCourses={setChoosenCourses}
            error={needCourseError}
          />

          {needCourseError && (
            <Error>
              <MdError color="#DC2626" size={14} />
              <FormattedMessage id="error.message_add_one_course_banner" />
            </Error>
          )}

          <ActionButtonsContainer>
            <CancelButton to={"/admin/informa-academy/manage-homepage"}>
              <FaRegTrashAlt />
              <FormattedMessage id="cancel" />
            </CancelButton>
            <PreviewButton>
              <FaRegEye />
              <FormattedMessage id="preview" />
            </PreviewButton>
          </ActionButtonsContainer>
        </form>
      </FormContainer>
    </Container>
  );
}

function AddIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="18"
      height="19"
      fill="none"
      viewBox="0 0 18 19"
    >
      <g stroke="#024" strokeWidth="1.5" clipPath="url(#a)">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 9.14H6m3 3v-6"
        />
        <path d="M1.5 9.14a7.5 7.5 0 1 0 15 0 7.5 7.5 0 0 0-15 0Z" />
      </g>
      <defs>
        <clipPath id="a">
          <path fill="#fff" d="M18 .14v18H0v-18z" />
        </clipPath>
      </defs>
    </svg>
  );
}
